/**
 * downloadFilename.js
 *
 * One place that builds clean, self-describing, filesystem-safe download names
 * for every document export surface (Minutes, resolutions, document drafts,
 * reports, admin order detail). A downloaded file should always say WHICH
 * company, WHAT document, and WHEN — so any user, handling any client, can tell
 * files apart without opening them, e.g.:
 *
 *   ABBHANI-FOODS-PRIVATE-LIMITED_Board-Minutes_2nd_2026-06-22.pdf
 *
 * `buildDocBasename` returns the basename WITHOUT extension; the caller appends
 * `.pdf` / `.txt` / `.html`. The same basename is fed to both useDocumentExport
 * (txt/html/copy) and the numbered-PDF download so all formats of one document
 * share a name.
 */

// One segment → safe slug. Any run of non-alphanumerics collapses to a single
// hyphen; casing is preserved for readability (company names stay legible).
export function slugifySegment(value) {
  return String(value ?? '')
    .normalize('NFKD')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Local calendar day (not UTC) so the stamp matches what the user sees.
export function todayStamp() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * @param {Object} parts
 * @param {string} [parts.company]    — company / client name (leads the name so a
 *                                       downloads folder groups by client)
 * @param {string} [parts.docType]    — document title or type (e.g. "Board Minutes")
 * @param {string} [parts.identifier] — optional disambiguator (meeting no., draft id)
 * @param {string} [parts.date]       — ISO date to stamp; defaults to today (local)
 * @returns {string} extension-less basename, never empty ("document" as last resort)
 */
export function buildDocBasename({ company, docType, identifier, date } = {}) {
  const stamp = date ? slugifySegment(date) : todayStamp();
  const parts = [company, docType, identifier, stamp]
    .map(slugifySegment)
    .filter(Boolean);
  let base = parts.join('_') || 'document';
  const MAX = 120; // keep well under Windows MAX_PATH once a folder + extension are added
  if (base.length > MAX) base = base.slice(0, MAX).replace(/[-_]+$/, '');
  return base;
}
